import type { Spritesheet, Texture } from "pixi.js";
import type { TextureAtlas } from "@esotericsoftware/spine-pixi-v8";
import { SkeletonDataCache, SpriteFontDefinition } from "@slot/presentation";
import type { Log, SpriteFontConfigJson } from "@slot/presentation";
import type { SpineArtUrls } from "@slot/presentation/rive-spine";
import type { BootLaunchPayload, BrandedAssetResolver } from "@slot/game-shell";

/** Every skeleton in `animations/spine/` shares this atlas (symbols, cannon, panels, the background). */
const SHARED_SPINE_ATLAS = "animations/spine/atlas/atlas.atlas";

/**
 * The designer's Rive art as rive-spine's Spine conversion (`assets-src/game-spine`): never loaded as a Pixi bundle.
 * Each file's skeleton, atlas and pages (its folder's `skeleton.json`, `atlas.atlas`, `pages.pages`) are fetched by URL
 * as it loads, the pages decoded as Rive decodes them (`SpineArtboard`).
 */
const SPINE_BUNDLE = "game-spine";
/** The pirate captain's folder there. */
const CAPTAIN_ART = "pirate-captain";

/**
 * Everything the round's visuals need, resolved ONCE from the loaded bundles through the branded
 * resolver (operator bundle first for allowlisted paths). Scene builders, views and features take
 * this bag; nothing else touches `AssetRegistry`. Missing optional art resolves to null and is logged,
 * so the game still boots without it. The symbols sheet is the one required entry: `getAssetOrThrow`
 * throws when it is missing, uncaught, and the boot boundary shows the boot-failure popup.
 */
export interface GameVisualAssets {
  readonly symbols: Spritesheet;
  readonly fonts: {
    readonly goldenFont: SpriteFontDefinition | null;
    readonly goldenNumbers1: SpriteFontDefinition | null;
  };
  /** Skeletons on the shared atlas, by registry path (e.g. `animations/spine/skull.skeleton`). */
  readonly skeletons: SkeletonDataCache;
  /** Where the pirate captain's files are served; null (logged) when the build lacks one: no captain then. */
  readonly captainArt: SpineArtUrls | null;
  readonly textures: {
    readonly baseGridFrame: Texture | null;
    readonly featureGridFrame: Texture | null;
    readonly background: Texture | null;
    readonly glow: Texture | null;
    /** Feature-finished OFFER panel background (`backgrounds/featurePresenterPanelBg`). */
    readonly featurePanelBg: Texture | null;
    /** Shell nine-slice borders (offer box + its Buy / Decline buttons). */
    readonly border16: Texture | null;
    readonly border32: Texture | null;
    /** The glow that flares at the cannon's mouth on a scatter drop (`effects/screenLight`, drawn in Screen mode). */
    readonly screenLight: Texture | null;
  };
  readonly sheets: {
    readonly removalBurst: Spritesheet | null;
    readonly gamble: Spritesheet | null;
  };
}

export function loadGameVisualAssets(input: { payload: BootLaunchPayload; log: Log }): GameVisualAssets {
  const { payload, log } = input;
  const branded: BrandedAssetResolver = payload.brandedAssets;
  const optional = <T>(path: string, what: string): T | null => {
    const asset = branded.getAssetOrNull<T>(path);
    if (asset === null) log(`[GameVisualAssets] ${what} "${path}" not loaded`);
    return asset;
  };
  // The shell's art is not brandable: read from `shell-ui` directly, logged on a miss like the rest.
  const shellUiTexture = (path: string): Texture | null => {
    const texture = branded.getFromBundleOrNull<Texture>("shell-ui", path);
    if (texture === null) log(`[GameVisualAssets] shell-ui texture "${path}" not loaded`);
    return texture;
  };

  const sharedAtlas = optional<TextureAtlas>(SHARED_SPINE_ATLAS, "spine atlas");
  const skeletons = new SkeletonDataCache((path) => {
    const skeleton = branded.getAssetOrNull<unknown>(path);
    return skeleton && sharedAtlas ? { skeleton, atlas: sharedAtlas } : null;
  }, log);

  const captainUrl = (file: string): string | null => payload.assets.resolveUrl(SPINE_BUNDLE, `${CAPTAIN_ART}/${file}`);
  const [captainSkeleton, captainAtlas, captainPages] = [captainUrl("skeleton"), captainUrl("atlas"), captainUrl("pages")];
  const captainArt = captainSkeleton && captainAtlas && captainPages ? { skeleton: captainSkeleton, atlas: captainAtlas, pages: captainPages } : null;
  if (!captainArt) log(`[GameVisualAssets] no "${SPINE_BUNDLE}/${CAPTAIN_ART}" skeleton, atlas or pages in the manifest: no captain`);

  return {
    symbols: branded.getAssetOrThrow<Spritesheet>("symbols/atlases/symbols"),
    fonts: {
      goldenFont: loadSpriteFont(branded, "goldenFont", log),
      goldenNumbers1: loadSpriteFont(branded, "goldenNumbers1", log),
    },
    skeletons,
    captainArt,
    textures: {
      baseGridFrame: optional<Texture>("backgrounds/BaseGridFrame", "texture"),
      featureGridFrame: optional<Texture>("backgrounds/FeatureGridFrame", "texture"),
      background: optional<Texture>("backgrounds/background", "texture"),
      glow: shellUiTexture("sprites/essentials/glow"),
      featurePanelBg: optional<Texture>("backgrounds/featurePresenterPanelBg", "texture"),
      border16: shellUiTexture("sprites/essentials/borders/border16px"),
      border32: shellUiTexture("sprites/essentials/borders/border32px"),
      screenLight: optional<Texture>("effects/screenLight", "texture"),
    },
    sheets: {
      removalBurst: optional<Spritesheet>("symbols/effects/remove/spritesheet", "spritesheet"),
      gamble: optional<Spritesheet>("gamble/gamblePanel", "spritesheet"),
    },
  };
}

function loadSpriteFont(branded: BrandedAssetResolver, name: string, log: Log): SpriteFontDefinition | null {
  const sheet = branded.getAssetOrNull<Spritesheet>(`fonts/atlases/${name}`);
  const config = branded.getAssetOrNull<SpriteFontConfigJson>(`fonts/configs/${name}`);
  if (!sheet || !config) {
    log(`[GameVisualAssets] sprite font "${name}" not loaded (sheet=${!!sheet} config=${!!config})`);
    return null;
  }
  return SpriteFontDefinition.fromConfig({ config, atlas: sheet.textures, log });
}
